// What to tell the CSM when /superadmin/bvbp-apply fails or succeeds.

// Per-document limit enforced by rest-api (multer) before anything goes to Meta.
export const BVBP_FILE_LIMIT_BYTES = 5 * 1024 * 1024;
// The nginx in front of rest-api rejects bodies above this with a 413 that has
// no CORS header, so the browser only sees "no response". Raise it when infra
// raises client_max_body_size.
export const BVBP_UPLOAD_LIMIT_BYTES = 1024 * 1024;

const toMb = (bytes) => (bytes / (1024 * 1024)).toFixed(1);

export const bvbpErrorMessage = (error) => {
  const response = error?.response;
  if (!response) {
    // A missing response can also be a dropped connection after Meta already
    // accepted the documents, so never claim nothing was sent.
    return `No response from our server, so we can't tell whether Meta got the documents. Most often the upload is over ${toMb(
      BVBP_UPLOAD_LIMIT_BYTES
    )} MB and was blocked before reaching us. Check the project's KYC status before retrying, so you don't use another of the client's 3 Meta attempts.`;
  }

  const data = response.data;
  // Our own checks (blockers, files, BM id, gateway refusals):
  // { error: { error_user_msg } }.
  if (data?.error?.error_user_msg) return data.error.error_user_msg;
  // Older backends pass the access gateway's refusal through as-is.
  if (typeof data?.error === "string") {
    return `Blocked by our Meta access gateway (${response.status} ${data.error}). Nothing was sent to Meta.`;
  }
  // A raw Meta error body: { error: { message } }.
  if (data?.error?.message) return `Meta: ${data.error.message}`;
  // Meta's rejection and the permission check: { message }.
  if (data?.message) return `Meta / server: ${data.message}`;
  // Blocked before our server (e.g. a firewall): plain text like "Forbidden".
  if (typeof data === "string" && data.trim()) {
    return `Request blocked (${response.status} ${data.trim()}). Nothing was sent to Meta.`;
  }
  return `Something went wrong (${response.status}).`;
};

// Shown under the document list; null when the files are fine.
export const bvbpUploadSizeWarning = (files) => {
  if (files.some((file) => file?.size > BVBP_FILE_LIMIT_BYTES)) {
    return "Each document must be 5 MB or smaller. Remove the larger file.";
  }
  const total = files.reduce((sum, file) => sum + (file?.size || 0), 0);
  if (total > BVBP_UPLOAD_LIMIT_BYTES) {
    return `Documents total ${toMb(total)} MB. Uploads over 1 MB are currently blocked before our server; compress the files first.`;
  }
  return null;
};

export const bvbpSuccessMessage = (data) =>
  data?.verification_attempts
    ? `Submitted to Meta (attempt ${data.verification_attempts} of 3).`
    : "Submitted to Meta.";
